import { useEffect, useRef, useState } from "react";

export type PreviewState = "loading" | "loaded" | "failed";

/**
 * Shows a document the desk is about to decide on, and reports whether it actually appeared:
 * Approve waits for "loaded", and a file that cannot be shown says so and keeps it off.
 *
 * An image reports through its own load and error events. A PDF is fetched first and shown
 * from the bytes: storage hands out links that say "download", which a frame would obey, and
 * a missing file would otherwise load as an error page and look fine. A file the browser
 * cannot read is reported as not shown.
 */
export default function FilePreview({
  url,
  kind,
  name,
  onState,
  className = "",
}: {
  url?: string | null;
  kind: "pdf" | "image";
  name: string;
  onState: (state: PreviewState) => void;
  className?: string;
}) {
  const reported = useRef<string>("");
  const report = (state: PreviewState) => {
    const key = `${url}:${state}`;
    if (reported.current === key) return;
    reported.current = key;
    onState(state);
  };

  const [fetched, setFetched] = useState<{ as: "pdf" | "image"; url: string } | null>(null);
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    reported.current = "";
    setFetched(null);
    setBroken(false);
    if (!url) {
      report("failed");
      return;
    }
    report("loading");
    if (kind !== "pdf") return;
    let live = true;
    let made: string | null = null;
    fetch(url, { mode: "cors" })
      .then((res) => {
        if (!res.ok) throw new Error("not found");
        return res.blob();
      })
      .then(async (bytes) => {
        if (!live) return;
        // What the file is comes from its own first bytes, never from its name or the type it was filed under.
        const head = new Uint8Array(await bytes.slice(0, 1024).arrayBuffer());
        const startsWith = (...sig: number[]) => sig.every((v, i) => head[i] === v);
        const isPdf = new TextDecoder("latin1").decode(head).includes("%PDF-");
        const imageType = startsWith(0x89, 0x50, 0x4e, 0x47) ? "image/png" : startsWith(0xff, 0xd8, 0xff) ? "image/jpeg" : startsWith(0x47, 0x49, 0x46, 0x38) ? "image/gif" : startsWith(0x52, 0x49, 0x46, 0x46) && head[8] === 0x57 ? "image/webp" : null;
        if (!live) return;
        if (!isPdf && !imageType) throw new Error("not a file that can be shown");
        made = URL.createObjectURL(new Blob([bytes], { type: isPdf ? "application/pdf" : (imageType as string) }));
        setFetched({ as: isPdf ? "pdf" : "image", url: made });
      })
      .catch(() => {
        if (live) report("failed");
      });
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, kind]);

  if (!url) {
    return (
      <p role="alert" className="flex min-h-[200px] flex-1 items-center justify-center p-4 text-center text-sm text-[#B00020]">
        The file could not be shown.
      </p>
    );
  }
  return (
    <div className={`relative flex min-h-[300px] flex-1 items-center justify-center overflow-hidden rounded-lg border bg-muted ${className}`}>
      {kind === "pdf" ? (
        fetched?.as === "pdf" ? (
          <iframe src={fetched.url} title={name} className="h-full min-h-[360px] w-full" onLoad={() => report("loaded")} />
        ) : fetched?.as === "image" ? (
          <img src={fetched.url} alt={name} className="max-h-[420px] w-full object-contain" onLoad={() => report("loaded")} onError={() => report("failed")} />
        ) : null
      ) : (
        <img src={url} alt={name} className={`max-h-[420px] w-full object-contain ${broken ? "invisible" : ""}`} onLoad={() => report("loaded")} onError={() => { setBroken(true); report("failed"); }} />
      )}
    </div>
  );
}

/** What to say under a preview. */
export function PreviewNote({ state }: { state: PreviewState }) {
  if (state === "failed") {
    return (
      <p role="alert" className="text-center text-sm text-[#B00020]">
        The file could not be shown.
      </p>
    );
  }
  if (state === "loading") {
    return (
      <p role="status" className="text-center text-sm text-[#555555]">
        Loading the file...
      </p>
    );
  }
  return null;
}
